import { useState, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { apiService, ApiError } from '../services/api'
import { formatNumber } from '../utils/formatters'
import { useDateRange } from '../context/DateRangeContext'
import { useNotification } from '../context/NotificationContext'
import { useLanguage } from '../context/LanguageContext'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface TimeSeriesChartProps {
  selectedUser?: string
}

export default function TimeSeriesChart({ selectedUser = '' }: TimeSeriesChartProps) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [granularity, setGranularity] = useState<'hourly' | 'daily'>('daily')
  const [userPreference, setUserPreference] = useState<'hourly' | 'daily' | null>(null)
  const { startDate, endDate, daysDiff } = useDateRange()
  const { showNotification } = useNotification()
  const { t, language } = useLanguage()

  // Fetch data when dates change or user filter changes
  useEffect(() => {
    if (startDate && endDate) {
      loadData()
    }
  }, [startDate, endDate, granularity, selectedUser])

  // Determine granularity based on date range and user preference
  useEffect(() => {
    if (daysDiff > 30) {
      // Force daily when over 30 days (hourly not available)
      setGranularity('daily')
    } else if (userPreference) {
      // User has made a choice, respect it
      setGranularity(userPreference)
    }
    // Default is 'daily' (set in useState), keep it unless user changes
  }, [daysDiff, userPreference])

  // Handle user clicking the toggle
  const handleGranularityChange = (newGranularity: 'hourly' | 'daily') => {
    setUserPreference(newGranularity)
    setGranularity(newGranularity)
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const timeSeries = await apiService.getTimeSeriesBreakdown(startDate, endDate, granularity, 'app', selectedUser)
      setData(timeSeries)
    } catch (error) {
      console.error('Error loading time series:', error)

      if (error instanceof ApiError) {
        const [title, ...messageParts] = error.userMessage.split(': ')
        showNotification('error', title, messageParts.join(': '))
      } else {
        showNotification(
          'error',
          t.errors.failedToLoadChartData,
          t.errors.tryRefreshing
        )
      }
    } finally {
      setLoading(false)
    }
  }

  // Format dates for display
  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    const locale = language === 'nl' ? 'nl-NL' : 'en-US'
    if (granularity === 'hourly') {
      return date.toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    })
  }

  // Prepare chart data - stacked lines per app
  const getChartData = () => {
    if (data.length === 0) return { labels: [], datasets: [] }

    // Get unique timestamps and apps
    const timestamps = [...new Set(data.map(item => item.timestamp || item.date))].sort()
    const allApps = [...new Set(data.map(item => item.app))].sort()

    // Create a map for quick lookup and calculate totals
    const dataMap = new Map()
    const appTotals = new Map()

    data.forEach(item => {
      const key = `${item.timestamp || item.date}_${item.app}`
      dataMap.set(key, item.activity_count)

      // Track total for each app
      const currentTotal = appTotals.get(item.app) || 0
      appTotals.set(item.app, currentTotal + item.activity_count)
    })

    // Filter out apps with zero total events and sort by total (largest to smallest)
    // In stacked bars, first dataset renders at bottom, so we want largest first
    const apps = allApps
      .filter(app => (appTotals.get(app) || 0) > 0)
      .sort((a, b) => (appTotals.get(b) || 0) - (appTotals.get(a) || 0))

    // NextNovate color palette
    const colors = [
      '#FBB300', // Yellow (primary)
      '#5ABFEC', // Blue
      '#89E185', // Green
      '#F29100', // Orange
      '#87E2FF', // Bright Blue
      '#76CB6E', // Bright Green
      '#F5CE7F', // Light Yellow
      '#A7D5F0', // Light Blue
      '#9EC588', // Light Green
      '#959DA9', // Dark Grey
      '#2B2F3A', // Anthracite
      '#1A1A1A', // Black
    ]

    // Create dataset for each app - smallest to largest
    const datasets = apps.map((app, index) => {
      const appData = timestamps.map(timestamp => {
        const key = `${timestamp}_${app}`
        return dataMap.get(key) || 0
      })

      const color = colors[index % colors.length]

      return {
        label: app,
        data: appData,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
      }
    })

    return {
      labels: timestamps.map(ts => formatDate(ts)),
      datasets,
    }
  }

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y
            // Filter out zero values
            if (value === 0) return null
            const label = context.dataset.label || ''
            return `${label}: ${formatNumber(value)}`
          },
        },
        filter: (tooltipItem) => {
          // Only show non-zero values
          return tooltipItem.parsed.y !== 0
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10,
          },
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        stacked: true,
        title: {
          display: true,
          text: t.timeChart.activities,
          font: {
            weight: 'bold',
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: (value) => formatNumber(value as number),
        },
        beginAtZero: true,
      },
    },
  }

  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const chartData = data.length > 0 ? getChartData() : { labels: [], datasets: [] }

  const hourlyAvailable = daysDiff <= 30

  return (
    <div>
      {/* Granularity Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            <button
              onClick={() => handleGranularityChange('hourly')}
              disabled={!hourlyAvailable}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                granularity === 'hourly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : hourlyAvailable
                    ? 'text-gray-600 hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              {t.timeChart.perHour}
            </button>
            <button
              onClick={() => handleGranularityChange('daily')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                granularity === 'daily'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t.timeChart.perDay}
            </button>
          </div>
          {!hourlyAvailable && (
            <span className="text-xs text-gray-500">
              {t.timeChart.hourlyOnlyAvailable}
            </span>
          )}
        </div>
      </div>

      {/* Chart */}
      {data.length > 0 ? (
        <div style={{ height: '400px' }}>
          <Bar data={chartData} options={options} />
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">{t.timeChart.noDataAvailable}</p>
          <p className="text-sm mt-2">{t.timeChart.tryDifferentRange}</p>
        </div>
      )}

      {/* Summary Stats */}
      {data.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-gray-600">{t.timeChart.totalActivities}</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(data.reduce((sum, item) => sum + item.activity_count, 0))}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-gray-600">{t.timeChart.maxActivities} {granularity === 'hourly' ? t.timeChart.hour : t.timeChart.day}</p>
            <p className="text-2xl font-bold text-green-600">
              {formatNumber(Math.max(...data.map((item) => item.activity_count)))}
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-sm text-gray-600">{t.timeChart.numberOfApps}</p>
            <p className="text-2xl font-bold text-yellow-600">
              {[...new Set(data.map(item => item.app))].length}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
